import DoneIcon from '@mui/icons-material/Done';
import ErrorIcon from '@mui/icons-material/Error';
import UpdateIcon from '@mui/icons-material/Update';
import { Box, Chip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  PaidolUser,
  ReimbursementTransactionStatus,
  ReimbursementTransactionType,
  ReimbursementTransaction,
  ReimbursementTransactionCheckrunStatus,
} from 'API';
import type { GetCardsCardholderPayload } from 'CDK_API';

export enum ReimbursementType {
  FINANCIAL = 'FINANCIAL',
  MILEAGE = 'MILEAGE',
}
export type ReimbursementTabType = {
  id: string;
  label: string;
  status: ReimbursementTransactionStatus;
};

export type FINANCIAL_TYPE_COLUMN = {
  id: string;
  label: string;
  align?: 'left' | 'center' | 'right' | 'justify' | 'inherit';
};

export const REIMBURSEMENT_TABS = [
  {
    id: 'requested',
    label: 'reimbursementPage.tabs.requested',
    status: ReimbursementTransactionStatus.REQUESTED,
  },
  // SEE-943: reviewed by a group admin, awaiting final admin approval.
  {
    id: 'reviewed',
    label: 'reimbursementPage.tabs.reviewed',
    status: ReimbursementTransactionStatus.REVIEWED,
  },
  {
    id: 'approved',
    label: 'reimbursementPage.tabs.approved',
    status: ReimbursementTransactionStatus.APPROVED,
  },
  {
    id: 'exported',
    label: 'reimbursementPage.tabs.exported',
    status: ReimbursementTransactionStatus.EXPORTED,
  },
  { id: 'denied', label: 'reimbursementPage.tabs.denied', status: ReimbursementTransactionStatus.DENIED },
  //{ id: 'contested', label: 'reimbursementPage.tabs.contested', status: ReimbursementTransactionStatus.CONTESTED },
  //{ id: 'paid', label: 'reimbursementPage.tabs.paid', status: ReimbursementTransactionStatus.PAID },
];

// SEE-943: group admins work only the review step — Requested (actionable)
// and Reviewed (view-only).
export const GROUP_ADMIN_REIMBURSEMENT_TAB_IDS = ['requested', 'reviewed'];

export const FINANCIAL_TYPE_COLUMNS: FINANCIAL_TYPE_COLUMN[] = [
  { id: 'transactionDate', label: 'reimbursementPage.table.columns.transactionDate' },
  { id: 'teamMember', label: 'reimbursementPage.table.columns.teamMember' },
  { id: 'amount', label: 'reimbursementPage.table.columns.amount', align: 'right' },
  { id: 'merchantName', label: 'reimbursementPage.table.columns.merchantName' },
  { id: 'transactionType', label: 'reimbursementPage.table.columns.transactionType' },
  { id: 'status', label: 'reimbursementPage.table.columns.status' },
];

export const REQUESTED_TAB_COLUMNS = [
  { id: 'requestDate', label: 'reimbursementPage.table.columns.requestDate' },
  ...FINANCIAL_TYPE_COLUMNS,
];

export const APPROVED_TAB_COLUMNS = [
  { id: 'decisionDate', label: 'reimbursementPage.table.columns.decisionDate' },
  ...FINANCIAL_TYPE_COLUMNS,
];

export const EXPORTED_TAB_COLUMNS = [
  { id: 'decisionDate', label: 'reimbursementPage.table.columns.decisionDate' },
  ...FINANCIAL_TYPE_COLUMNS,
];

export const MILEAGE_TYPE_COLUMNS = [
  { id: 'date', label: 'reimbursementPage.table.columns.date' },
  { id: 'teamMember', label: 'reimbursementPage.table.columns.teamMember' },
  { id: 'distance', label: 'reimbursementPage.table.columns.distance' },
  { id: 'expenseType', label: 'reimbursementPage.table.columns.expenseType' },
];

export const receiptItemsRadioOptions = [
  {
    value: 'TOTAL',
    label: 'reimbursementPage.submitForm.receiptLineItemsOptions.total',
  },
  {
    value: 'SINGLE',
    label: 'reimbursementPage.submitForm.receiptLineItemsOptions.single',
  },
  {
    value: 'MULTIPLE',
    label: 'reimbursementPage.submitForm.receiptLineItemsOptions.multiple',
  },
];

export { transactionMethodSelectOptions } from './reimbursementFormatting';

export const reflectMap: Record<string, string> = {
  TOTAL: 'reimbursementPage.submitForm.receiptLineItemsOptions.total',
  SINGLE: 'reimbursementPage.submitForm.receiptLineItemsOptions.single',
  MULTIPLE: 'reimbursementPage.submitForm.receiptLineItemsOptions.multiple',
};

export const mapPaidolUserToCardholder = (
  paidolUser?: PaidolUser | null
): GetCardsCardholderPayload | undefined => {
  if (!paidolUser) return undefined;

  let fullCardholderName = paidolUser?.user?.first_name;
  if (fullCardholderName && paidolUser?.user?.last_name)
    fullCardholderName += ` ${paidolUser?.user?.last_name}`;

  return {
    id: paidolUser?.user_id ?? '',
    name: fullCardholderName,
    email: paidolUser?.user?.email ?? paidolUser?.email ?? '',
    picture: paidolUser?.user?.picture,
  };
};

export const formatTransactionType = (
  transactionType: ReimbursementTransactionType | null | undefined
): string => {
  if (!transactionType) return '';

  switch (transactionType) {
    case ReimbursementTransactionType.CREDIT_CARD:
      return 'reimbursementPage.transactionTypes.creditCard';
    case ReimbursementTransactionType.DEBIT_CARD:
      return 'reimbursementPage.transactionTypes.debitCard';
    case ReimbursementTransactionType.CHECK:
      return 'reimbursementPage.transactionTypes.check';
    case ReimbursementTransactionType.CASH:
      return 'reimbursementPage.transactionTypes.cash';
    // 'NA' is a valid enum value at the API layer but isn't in the generated
    // TS enum (no Amplify codegen for this change), so match the literal.
    case 'NA' as ReimbursementTransactionType:
      return 'reimbursementPage.transactionTypes.na';
    default:
      return transactionType;
  }
};

// Speedchain_Status-Pill from Design Kit v2 (node 2863:28525): 22px tall,
// 16px radius, 6/8px side padding, 12px bold label, no icon. Colors are the
// kit's semantic tokens, inlined because the app theme has no equivalents.
function StatusPill({
  label,
  color,
  bg,
  dashedBorder = false,
}: {
  label: string;
  color: string;
  bg?: string;
  dashedBorder?: boolean;
}) {
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: 22,
        borderRadius: '16px',
        pl: '6px',
        pr: '8px',
        py: '2px',
        fontSize: 12,
        // The kit says Bold, but it's authored in SF Pro — Poppins 700 reads
        // much heavier at 12px, so 600 is the perceptual match.
        fontWeight: 600,
        lineHeight: '16px',
        letterSpacing: 0,
        whiteSpace: 'nowrap',
        backgroundColor: bg,
        color,
        border: dashedBorder ? `1px dashed ${color}` : undefined,
      }}
    >
      {label}
    </Box>
  );
}

interface RenderStatusChipProps {
  reimbursement: ReimbursementTransaction;
}

export const RenderStatusChip = ({ reimbursement }: RenderStatusChipProps) => {
  const { t } = useTranslation();

  if (reimbursement.checkrunStatus) {
    switch (reimbursement.checkrunStatus) {
      case ReimbursementTransactionCheckrunStatus.PAYMENT_INITIATED:
      case ReimbursementTransactionCheckrunStatus.PROCESSING:
        return (
          <Chip
            label={t('reimbursementPage.status.processing')}
            color="info"
            size="small"
            sx={{ px: 1 }}
            icon={<UpdateIcon />}
          />
        );
      case ReimbursementTransactionCheckrunStatus.PAYMENT_FAILED:
        return (
          <Chip
            label={t('reimbursementPage.status.paymentFailed')}
            color="error"
            size="small"
            sx={{ px: 1 }}
            icon={<ErrorIcon />}
          />
        );
      case ReimbursementTransactionCheckrunStatus.PAYMENT_SUCCESSFUL:
        return (
          <Chip
            label={t('reimbursementPage.status.paymentSuccessful')}
            color="success"
            size="small"
            sx={{ px: 1 }}
            icon={<DoneIcon />}
          />
        );
    }
  }

  if (reimbursement.status) {
    switch (reimbursement.status) {
      case ReimbursementTransactionStatus.REQUESTED:
        return <StatusPill label={t('reimbursementPage.status.requested')} bg="#D4E4FF" color="#193059" />;
      case ReimbursementTransactionStatus.REVIEWED:
        return <StatusPill label={t('reimbursementPage.status.reviewed')} bg="#F2F7F2" color="#008000" />;
      case ReimbursementTransactionStatus.APPROVED:
        return <StatusPill label={t('reimbursementPage.status.approved')} bg="#F2F7F2" color="#008000" />;
      case ReimbursementTransactionStatus.EXPORTED:
        return <StatusPill label={t('reimbursementPage.status.exported')} color="#777779" dashedBorder />;
      case ReimbursementTransactionStatus.DENIED:
        return <StatusPill label={t('reimbursementPage.status.denied')} bg="#FFF0F0" color="#B30000" />;
      // CONTESTED and PAID are legacy statuses nothing writes anymore and the
      // design kit has no pill for them — they keep the old chips.
      case ReimbursementTransactionStatus.CONTESTED:
        return (
          <Chip
            label={t('reimbursementPage.status.contested')}
            color="warning"
            size="small"
            sx={{ px: 1, color: '#FFF' }}
            icon={<ErrorIcon />}
          />
        );
      case ReimbursementTransactionStatus.PAID:
        return (
          <Chip
            label={t('reimbursementPage.status.paid')}
            color="success"
            size="small"
            sx={{ px: 1 }}
            icon={<DoneIcon />}
          />
        );
    }
  }

  // Return null if no status is found
  return null;
};
